/**
 * Decides whether the renderer's Supabase session is still usable.
 *
 * Kept free of app imports (supabase client, redux, window.electron) so the
 * decision logic is unit-testable on its own — see useSessionExpiryGuard for
 * the wiring.
 *
 * The one rule that matters: a session is only declared `expired` when the
 * auth server has actually REJECTED it (or there is no session while the
 * server is reachable). Being offline must never sign anyone out — the POS is
 * offline-first and a network blip mid-service is normal.
 */

export type SessionHealth =
  /** Session present and not expired. */
  | 'valid'
  /** Access token had expired and was refreshed successfully. */
  | 'refreshed'
  /** Server rejected the session, or none exists while online — sign out. */
  | 'expired'
  /** Can't tell right now (offline / auth server unreachable) — do nothing. */
  | 'unknown';

interface MinimalSession {
  expires_at?: number | null;
}

interface AuthLike {
  getSession(): Promise<{
    data: { session: MinimalSession | null };
    error: unknown;
  }>;
  refreshSession(): Promise<{
    data: { session: MinimalSession | null };
    error: unknown;
  }>;
}

// Refresh a little before the hard expiry so a token that dies mid-request
// doesn't count as valid.
const EXPIRY_LEEWAY_MS = 10_000;

/**
 * True when a supabase-js auth error means the server rejected the session
 * (invalid / already-used / revoked refresh token, missing session) rather
 * than a transient network or server failure. AuthRetryableFetchError carries
 * status 0 (network) or 5xx, so it never matches.
 */
export function isSessionRejectedError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const { status, name } = error as { status?: unknown; name?: unknown };
  if (name === 'AuthRetryableFetchError') return false;
  if (name === 'AuthSessionMissingError') return true;
  return status === 400 || status === 401 || status === 403;
}

export async function checkSessionHealth(
  auth: AuthLike,
  isAuthServerReachable: () => Promise<boolean>,
  now: number = Date.now(),
): Promise<SessionHealth> {
  const {
    data: { session },
    error: getSessionError,
  } = await auth.getSession();

  if (!session) {
    // getSession() itself tries a refresh for an expired token; if the server
    // rejected it the session is already gone and we land here.
    // A transient error (offline, 5xx) leaves the stored session in place —
    // supabase-js only discards it on a genuine rejection.
    if (getSessionError) {
      return isSessionRejectedError(getSessionError) ? 'expired' : 'unknown';
    }
    // No session at all (e.g. a user restored offline from the cached
    // profile). Only force sign-in once the server is actually reachable.
    return (await isAuthServerReachable()) ? 'expired' : 'unknown';
  }

  const expiresAtMs = session.expires_at ? session.expires_at * 1000 : null;
  if (expiresAtMs === null || expiresAtMs - EXPIRY_LEEWAY_MS > now) {
    return 'valid';
  }

  const { data, error } = await auth.refreshSession();
  if (error) return isSessionRejectedError(error) ? 'expired' : 'unknown';
  return data.session ? 'refreshed' : 'unknown';
}
